/**
 * ConfirmModal — in-app modal replacing native window.confirm / alert.
 *
 * Ported to TSX + Nordic styling, framer-motion removed (CSS transition via
 * the `view-in` entrance + a simple fade backdrop). Variants: confirm / alert
 * / success / danger.
 */
import type { ReactElement } from "react";
import { AlertTriangle, CheckCircle2, Info, X } from "lucide-react";

export type ConfirmModalVariant = "confirm" | "alert" | "success" | "danger";

export interface ConfirmModalProps {
  isOpen: boolean;
  title?: string;
  message?: string;
  variant?: ConfirmModalVariant;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}

export default function ConfirmModal({
  isOpen,
  title = "Confirm",
  message = "",
  variant = "confirm",
  confirmLabel,
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  if (!isOpen) return null;

  const isAlertOnly = variant === "alert" || variant === "success";
  const resolvedConfirmLabel =
    confirmLabel ??
    (variant === "danger" ? "Delete" : isAlertOnly ? "OK" : "Confirm");

  // Icon + accent color per variant, using Nordic tokens.
  const iconFor: Record<ConfirmModalVariant, ReactElement> = {
    confirm: <Info size={22} strokeWidth={1.5} style={{ color: "var(--accent)" }} />,
    alert: (
      <AlertTriangle size={22} strokeWidth={1.5} style={{ color: "var(--brass)" }} />
    ),
    success: (
      <CheckCircle2 size={22} strokeWidth={1.5} style={{ color: "var(--pos)" }} />
    ),
    danger: (
      <AlertTriangle size={22} strokeWidth={1.5} style={{ color: "var(--neg)" }} />
    ),
  };

  // Confirm-button background per variant.
  const confirmBg: Record<ConfirmModalVariant, string> = {
    confirm: "var(--accent)",
    alert: "var(--ink)",
    success: "var(--pos)",
    danger: "var(--neg)",
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 t-base"
        style={{ background: "rgba(20,24,28,0.45)" }}
        onClick={onCancel}
      />

      {/* Panel */}
      <div
        className="relative rounded-lg border hairline shadow-lg w-full max-w-md mx-4 overflow-hidden view-in"
        style={{ background: "var(--bg-elev)" }}
      >
        <button
          onClick={onCancel}
          className="absolute top-3 right-3 p-1 rounded t-base hover:bg-[var(--hover)] focus-ring"
          style={{ color: "var(--ink-3)" }}
          aria-label="Close"
        >
          <X size={16} strokeWidth={1.5} />
        </button>

        <div className="p-6 flex flex-col items-center text-center gap-3">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{ background: "var(--hover)" }}
          >
            {iconFor[variant] || iconFor.confirm}
          </div>

          <h3
            className="font-serif text-[18px] leading-tight"
            style={{ color: "var(--ink)" }}
          >
            {title}
          </h3>

          <p
            className="text-[13.5px] font-serif leading-relaxed whitespace-pre-line"
            style={{ color: "var(--ink-2)" }}
          >
            {message}
          </p>
        </div>

        <div className="px-6 pb-5 flex items-center justify-center gap-3">
          {!isAlertOnly && (
            <button
              onClick={onCancel}
              className="px-4 py-2 text-[12px] font-mono uppercase tracking-[0.12em] rounded border hairline t-base hover:bg-[var(--hover)] focus-ring"
              style={{ color: "var(--ink-2)" }}
            >
              {cancelLabel}
            </button>
          )}
          <button
            onClick={onConfirm}
            autoFocus
            className="px-4 py-2 text-[12px] font-mono uppercase tracking-[0.12em] rounded t-base focus-ring"
            style={{ background: confirmBg[variant] || confirmBg.confirm, color: "var(--bg)" }}
          >
            {resolvedConfirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
